import {
  ArrowDropDown,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  FormatSize,
  Redo,
  Undo,
} from '@mui/icons-material';
import { Box, IconButton, styled } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useAIDocumentEditorContext } from '../../../../contexts/AIDocumentEditorContext';
import { COLORS } from '../../../../utils/colors';
import { useEditorStyles } from '../../DocumentWorkspace/styles';
import { SIZE_WHITELIST } from '../../../../utils/helpers';

const getNumericalFontSize = (size: string) => parseInt(size, 10);

interface ToolbarProps {
  id: string;
}

const EditorToolbar = ({ id }: ToolbarProps) => {
  const [fontSize, setFontSize] = useState<string>('12px');
  const [isSizeVisible, setIsSizeVisible] = useState<boolean>(false);
  const [listTypesVisible, setListTypesVisible] = useState(false);
  const [alignTextVisible, setAlignTextVisible] = useState(false);

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [textFormatMenuVisible, setTextFormatMenuVisible] = useState(false);
  const [textFormat, setTextFormat] = useState<
    'Normal text' | 'Heading 1' | 'Heading 2' | 'Heading 3'
  >('Normal text');

  const styles = useEditorStyles();

  const { quillRef, selectedCard } = useAIDocumentEditorContext();

  const DOCUMENT_EDITOR_ICONS = [
    {
      Icon: Undo,
      label: 'Undo',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleUndo();
      },
    },
    {
      Icon: Redo,
      label: 'Redo',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleRedo();
      },
    },
    {
      Icon: FormatBold,
      label: 'Bold',
      active: false,
      handleClick: (event: any) => {
        event.stopPropagation();
        handleBold();
      },
    },
    {
      Icon: FormatItalic,
      label: 'Italic',
      active: false,
      handleClick: (event: any) => {
        event.stopPropagation();
        handleItalic();
      },
    },
    {
      Icon: FormatUnderlined,
      label: 'Underline',
      active: false,
      handleClick: (event: any) => {
        event.stopPropagation();
        handleUnderline();
      },
    },
    {
      Icon: FormatListBulleted,
      label: 'Bullet',
      handleClick: (event: any) => {
        event.stopPropagation();
        hideLists();
        setListTypesVisible(!listTypesVisible);
      },
    },
    {
      Icon: FormatAlignLeft,
      label: 'Align',
      handleClick: (event: any) => {
        hideLists();
        setAlignTextVisible(!alignTextVisible);
        event.stopPropagation();
      },
    },
    {
      Icon: FormatSize,
      label: 'Font Size',
      handleClick: (event: any) => {
        event.stopPropagation();
        hideLists();
        setIsSizeVisible(!isSizeVisible);
      },
    },
  ];

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    const updateFormattingStates = () => {
      const selection = editor.getSelection();
      if (selection) {
        const format = editor.getFormat(selection);
        setIsBold(!!format.bold);
        setIsItalic(!!format.italic);
        setIsUnderline(!!format.underline);
        setTextFormat(toLabelFromHeader(format.header));
      }
    };

    if (editor) {
      editor.on('text-change', updateFormattingStates);
      editor.on('selection-change', updateFormattingStates);
      editor.root.addEventListener('click', updateFormattingStates);

      updateFormattingStates();

      return () => {
        editor.off('text-change', updateFormattingStates);
        editor.off('selection-change', updateFormattingStates);
        editor.root.removeEventListener('click', updateFormattingStates);
      };
    }
  }, [quillRef, selectedCard]);

  const toLabelFromHeader = (h?: number | boolean) => {
    if (h === 1) return 'Heading 1';
    if (h === 2) return 'Heading 2';
    if (h === 3) return 'Heading 3';
    return 'Normal text';
  };

  const toHeaderFromLabel = (label: typeof textFormat) => {
    if (label === 'Heading 1') return 1;
    if (label === 'Heading 2') return 2;
    if (label === 'Heading 3') return 3;
    return false; // Quill: remove header format
  };

  const resetToolbar = () => {
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
    hideLists();
  };

  const hideLists = () => {
    setIsSizeVisible(false);
    setListTypesVisible(false);
    setAlignTextVisible(false);
    setTextFormatMenuVisible(false);
  };

  const handleBold = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      editor.format('bold', !format.bold);
      setIsBold(!format.bold);
    }
  };

  const handleItalic = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      editor.format('italic', !format.italic);
      setIsItalic(!format.italic);
    }
  };

  const handleUnderline = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      editor.format('underline', !format.underline);
      setIsUnderline(!format.underline);
    }
  };

  const handleBulletList = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      editor.format('list', format.list === 'bullet' ? false : 'bullet');
      setListTypesVisible(false);
    }
  };

  const handleNumberList = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      editor.format('list', format.list === 'ordered' ? false : 'ordered');
      setListTypesVisible(false);
    }
  };

  const handleAlignText = (alignment: string) => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      let newAlignment;
      if (alignment === 'left') {
        newAlignment =
          format.align === 'left' || !format.align ? undefined : 'left';
      } else {
        newAlignment = format.align === alignment ? undefined : alignment;
      }
      editor.format('align', newAlignment);
    }
  };

  const handleTextFormatChange = (label: typeof textFormat) => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const targetHeader = toHeaderFromLabel(label);
    const { header } = editor.getFormat();
    const isSame = (header ?? false) === targetHeader;

    editor.format('header', isSame ? false : targetHeader);
    setTextFormat(isSame ? 'Normal text' : label);
  };

  const handleUndo = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.history.undo();
    }
  };

  const handleRedo = () => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.history.redo();
    }
  };

  const handleFontSizeChange = (size: string) => {
    hideLists();
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.format('size', size);
      setFontSize(size);
    }
  };

  useEffect(() => {
    if (selectedCard) return;

    hideLists();
  }, [selectedCard]);

  return (
    <>
      {isSizeVisible && (
        <Box sx={styles.canvasSizeMenu}>
          {SIZE_WHITELIST.map((size) => {
            const numericalSize = getNumericalFontSize(size);
            return (
              <Box
                sx={styles.menuFont}
                key={size}
                display="flex"
                width="100%"
                bgcolor={
                  parseInt(fontSize) === numericalSize
                    ? COLORS.LIGHT_GRAY
                    : COLORS.WHITE
                }>
                <IconButton
                  sx={styles.MenuSize}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFontSizeChange(size);
                  }}>
                  {numericalSize}
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {listTypesVisible && (
        <Box sx={styles.canvasListMenu}>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleBulletList();
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatListBulleted />
            </IconButton>
          </Box>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNumberList();
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatListNumbered />
            </IconButton>
          </Box>
        </Box>
      )}

      {alignTextVisible && (
        <Box sx={styles.canvasAlignMenu}>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAlignText('');
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatAlignLeft />
            </IconButton>
          </Box>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAlignText('center');
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatAlignCenter />
            </IconButton>
          </Box>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAlignText('right');
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatAlignRight />
            </IconButton>
          </Box>
          <Box sx={styles.menuFont} display="block" width="100%">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAlignText('justify');
              }}
              sx={styles.MenuSize}
              disableRipple>
              <FormatAlignJustify />
            </IconButton>
          </Box>
        </Box>
      )}

      {DOCUMENT_EDITOR_ICONS.map(({ Icon, label, handleClick }, index) => {
        const isActive =
          label === 'Bold'
            ? isBold
            : label === 'Italic'
              ? isItalic
              : label === 'Underline'
                ? isUnderline
                : false;
        return (
          <Box
            bgcolor={
              selectedCard && isActive ? COLORS.LIGHT_GRAY : COLORS.WHITE
            }
            key={index}
            sx={{
              display: 'flex',
              height: '100%',
              marginLeft: '0.5rem',
              justifyContent: 'center',
              cursor: selectedCard ? 'pointer' : 'not-allowed',
            }}
            color="black"
            position="relative">
            <IconButton
              onClick={(e) => {
                handleClick(e);
              }}
              disabled={!selectedCard}
              color="inherit"
              sx={{
                margin: 'auto',
                padding: '.5rem',
                opacity: selectedCard ? 1 : 0.5,
                cursor: selectedCard ? 'pointer' : 'not-allowed',
              }}>
              <Icon sx={{ fontSize: '1.305rem' }} />
            </IconButton>
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          marginLeft: '0.5rem',
          position: 'relative',
          bgcolor: COLORS.WHITE,
        }}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            hideLists();
            setTextFormatMenuVisible((v) => !v);
          }}
          disabled={!selectedCard}
          sx={{
            px: 1,
            py: 0.5,
            opacity: selectedCard ? 1 : 0.5,
            cursor: selectedCard ? 'pointer' : 'not-allowed',
            display: 'flex',
            gap: 0.25,
          }}>
          <Box
            component="span"
            sx={{ fontSize: '.92rem', whiteSpace: 'nowrap' }}>
            {textFormat}
          </Box>
          <ArrowDropDown sx={{ fontSize: '1.305rem' }} />
        </IconButton>

        {textFormatMenuVisible && (
          <Box
            sx={{
              ...styles.canvasSizeMenu,
              left: '0',
              width: '10rem',
            }}>
            {(
              ['Normal text', 'Heading 1', 'Heading 2', 'Heading 3'] as const
            ).map((label) => (
              <Box
                key={label}
                sx={styles.menuFont}
                display="flex"
                width="100%"
                bgcolor={
                  textFormat === label ? COLORS.LIGHT_GRAY : COLORS.WHITE
                }>
                <IconButton
                  sx={styles.MenuSize}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTextFormatChange(label);
                  }}>
                  {/* Show a visual hint of levels */}
                  <Box
                    component="span"
                    sx={{
                      fontSize:
                        label === 'Heading 1'
                          ? '1.125rem'
                          : label === 'Heading 2'
                            ? '1.05rem'
                            : label === 'Heading 3'
                              ? '.975rem'
                              : '.92rem',
                      fontWeight: label === 'Normal text' ? 400 : 600,
                    }}>
                    {label}
                  </Box>
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default EditorToolbar;
